import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import ForgeIcon from '../components/ForgeIcon';
import StatusPill from '../components/StatusPill';
import LearningAdmin from '../components/LearningAdmin';
import TeamAdmin from '../components/TeamAdmin';

const defaultBrand = {
  brand_name: 'Forge',
  brand_subtitle: 'NYXN',
  logo_url: '/assets/foge_logo.png',
  favicon_url: '/favicon.ico',
  primary_color: '#00d4ff',
  accent_color: '#00ff9f',
  theme: 'dark',
};

const roleDetails = {
  FORGE_MASTER: {
    description: 'Administra la instancia, roles, marca, gobierno y excepciones.',
    bestFor: 'Responsable final del método Forge y sus decisiones operativas.',
  },
  CONTEXT_OWNER: {
    description: 'Define intención, contexto, alcance y criterios de aceptación.',
    bestFor: 'Product owners, líderes de dominio o quien trae el problema.',
  },
  ARCHITECT: {
    description: 'Cuida arquitectura, dependencias, decisiones técnicas y mapas de cambio.',
    bestFor: 'Arquitectos o tech leads que revisan dirección técnica.',
  },
  BUILDER: {
    description: 'Implementa specs, código, pruebas y PRs de entrega.',
    bestFor: 'Developers y agentes que construyen cambios concretos.',
  },
  QUALITY_GUARDIAN: {
    description: 'Evalúa calidad, riesgos, gates, red cases y evidencia de verificación.',
    bestFor: 'QA, reviewers técnicos y responsables de cumplimiento.',
  },
  PLATFORM_ENGINEER: {
    description: 'Opera infraestructura, CI, runtime, despliegues y controles de plataforma.',
    bestFor: 'DevOps, SRE, platform engineers y dueños del entorno.',
  },
  EXECUTIVE_SPONSOR: {
    description: 'Prioriza valor, inversión, riesgo ejecutivo y objetivos de negocio.',
    bestFor: 'Stakeholders, sponsors y dirección.',
  },
  SUPPORT: {
    description: 'Permite asistencia administrativa temporal para diagnóstico e intervención.',
    bestFor: 'Equipo NYXN o soporte autorizado por el cliente.',
  },
};

const roleAdvisorOptions = [
  {
    value: 'CONTEXT_OWNER',
    label: 'Define alcance y criterios',
    hint: 'Context Owner',
  },
  {
    value: 'ARCHITECT',
    label: 'Diseña arquitectura',
    hint: 'Architect',
  },
  {
    value: 'BUILDER',
    label: 'Construye código y PRs',
    hint: 'Builder',
  },
  {
    value: 'QUALITY_GUARDIAN',
    label: 'Valida calidad y riesgos',
    hint: 'Quality Guardian',
  },
  {
    value: 'PLATFORM_ENGINEER',
    label: 'Opera plataforma y CI',
    hint: 'Platform Engineer',
  },
  {
    value: 'EXECUTIVE_SPONSOR',
    label: 'Prioriza negocio',
    hint: 'Executive / Sponsor',
  },
  {
    value: 'SUPPORT',
    label: 'Da soporte temporal',
    hint: 'Soporte',
  },
  {
    value: 'FORGE_MASTER',
    label: 'Gobierna toda la instancia',
    hint: 'Forge Master',
  },
];

function RoleCheckboxes({ availableRoles, selectedRoles, onChange, labelPrefix }) {
  const toggle = (roleValue) => {
    if (selectedRoles.includes(roleValue)) {
      if (selectedRoles.length === 1) return;
      onChange(selectedRoles.filter((value) => value !== roleValue));
      return;
    }
    onChange([...selectedRoles, roleValue]);
  };

  return (
    <div className="admin-role-picker" aria-label={labelPrefix}>
      {availableRoles.map((role) => (
        <label key={role.value}>
          <input
            type="checkbox"
            checked={selectedRoles.includes(role.value)}
            onChange={() => toggle(role.value)}
          />
          <span>{role.label}</span>
        </label>
      ))}
    </div>
  );
}

function Admin() {
  const { apiUrl, userId, currentUser, brand } = useOutletContext();
  const [searchParams] = useSearchParams();
  const roleSetupRequested = searchParams.get('setup') === 'roles';
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [githubIntegration, setGithubIntegration] = useState(null);
  const [brandForm, setBrandForm] = useState(defaultBrand);
  const [inviteForm, setInviteForm] = useState({
    github_username: '',
    roles: ['CONTEXT_OWNER'],
    note: '',
  });
  const [loading, setLoading] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [managingGithubIntegration, setManagingGithubIntegration] = useState(false);
  const [message, setMessage] = useState(null);
  const [rolesOpen, setRolesOpen] = useState(false);
  const [roleAdvisor, setRoleAdvisor] = useState({
    responsibility: 'CONTEXT_OWNER',
    adminAccess: 'no',
  });

  const pendingInvites = useMemo(
    () => invitations.filter((invitation) => !invitation.accepted).length,
    [invitations]
  );

  const rolesByValue = useMemo(() => {
    const mapped = new Map();
    roles.forEach((role) => mapped.set(role.value, role));
    return mapped;
  }, [roles]);

  const recommendedRole = useMemo(() => {
    if (roleAdvisor.adminAccess === 'yes' && roleAdvisor.responsibility === 'SUPPORT') {
      return 'SUPPORT';
    }
    if (roleAdvisor.adminAccess === 'yes') {
      return 'FORGE_MASTER';
    }
    return roleAdvisor.responsibility;
  }, [roleAdvisor]);

  const roleMeta = (roleValue) => {
    const apiRole = rolesByValue.get(roleValue);
    return {
      value: roleValue,
      label: apiRole?.label || roleValue,
      admin: Boolean(apiRole?.admin),
      ...roleDetails[roleValue],
    };
  };

  const githubAction = (action) => githubIntegration?.actions?.[action] || {
    available: false,
    reason: 'La disponibilidad de esta acción aún no se pudo confirmar.',
  };

  useEffect(() => {
    if (brand) {
      setBrandForm({ ...defaultBrand, ...brand });
    }
  }, [brand]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    async function loadAdminData() {
      setLoading(true);
      setMessage(null);
      try {
        const [rolesRes, usersRes, invitesRes, brandRes, githubIntegrationRes] = await Promise.all([
          fetch(`${apiUrl}/api/admin/roles`, { credentials: 'include' }),
          fetch(`${apiUrl}/api/admin/users?user_id=${userId}`, { credentials: 'include' }),
          fetch(`${apiUrl}/api/admin/invitations?user_id=${userId}`, { credentials: 'include' }),
          fetch(`${apiUrl}/api/admin/brand`, { credentials: 'include' }),
          fetch(`${apiUrl}/api/admin/github-integration?user_id=${userId}`, { credentials: 'include' }),
        ]);

        if (usersRes.status === 403 || invitesRes.status === 403) {
          setMessage({ type: 'error', text: 'Tu rol no tiene acceso administrativo.' });
          return;
        }

        if (!rolesRes.ok || !usersRes.ok || !invitesRes.ok || !brandRes.ok || !githubIntegrationRes.ok) {
          setMessage({ type: 'error', text: 'No se pudo cargar administración.' });
          return;
        }

        const [rolesData, usersData, invitesData, brandData, githubIntegrationData] = await Promise.all([
          rolesRes.json(),
          usersRes.json(),
          invitesRes.json(),
          brandRes.json(),
          githubIntegrationRes.json(),
        ]);

        if (!cancelled) {
          setRoles(rolesData);
          setUsers(usersData);
          setInvitations(invitesData);
          setBrandForm({ ...defaultBrand, ...brandData });
          setGithubIntegration(githubIntegrationData);
        }
      } catch (err) {
        if (!cancelled) {
          setMessage({ type: 'error', text: 'No se pudo conectar con el API.' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadAdminData();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, userId]);

  const updateRoles = async (targetUserId, nextRoles) => {
    setMessage(null);
    try {
      const res = await fetch(`${apiUrl}/api/admin/users/${targetUserId}/roles?user_id=${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ roles: nextRoles }),
        credentials: 'include',
      });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: 'error', text: data.detail || 'No se pudieron cambiar los roles.' });
        return;
      }

      setUsers((prev) => prev.map((user) => (user.id === data.id ? data : user)));
      setMessage({ type: 'success', text: `Roles actualizados para @${data.username}.` });
    } catch (err) {
      setMessage({ type: 'error', text: 'No se pudo conectar con el API.' });
    }
  };

  const createInvitation = async (event) => {
    event.preventDefault();
    setCreatingInvite(true);
    setMessage(null);
    try {
      const res = await fetch(`${apiUrl}/api/admin/invitations?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(inviteForm),
        credentials: 'include',
      });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: 'error', text: data.detail || 'No se pudo crear la invitación.' });
        return;
      }

      setInvitations((prev) => [data, ...prev]);
      setInviteForm({ github_username: '', roles: ['CONTEXT_OWNER'], note: '' });
      setMessage({ type: 'success', text: `Invitación creada para @${data.github_username}.` });
    } catch (err) {
      setMessage({ type: 'error', text: 'No se pudo conectar con el API.' });
    } finally {
      setCreatingInvite(false);
    }
  };

  const saveBrand = async (event) => {
    event.preventDefault();
    setSavingBrand(true);
    setMessage(null);
    try {
      const res = await fetch(`${apiUrl}/api/admin/brand?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(brandForm),
        credentials: 'include',
      });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: 'error', text: data.detail || 'No se pudo guardar la marca.' });
        return;
      }

      setBrandForm({ ...defaultBrand, ...data });
      setMessage({ type: 'success', text: 'Marca actualizada.' });
      window.location.reload();
    } catch (err) {
      setMessage({ type: 'error', text: 'No se pudo conectar con el API.' });
    } finally {
      setSavingBrand(false);
    }
  };

  const manageGithubIntegration = async (action) => {
    setManagingGithubIntegration(true);
    setMessage(null);
    try {
      const res = await fetch(`${apiUrl}/api/admin/github-integration/${action}?user_id=${userId}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setMessage({ type: 'error', text: data.detail || 'No se pudo actualizar la integración GitHub.' });
        return;
      }
      setGithubIntegration(data);
      const labels = {
        initialize: 'Se generó el secreto global de GitHub.',
        rotate: 'Se rotó el secreto global de GitHub.',
        validate: 'La integración global de GitHub quedó validada.',
        'sync-webhooks': 'Los webhooks de los repositorios conectados quedaron sincronizados.',
      };
      setMessage({ type: 'success', text: labels[action] });
    } catch (err) {
      setMessage({ type: 'error', text: 'No se pudo conectar con el API.' });
    } finally {
      setManagingGithubIntegration(false);
    }
  };

  const copyInvite = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setMessage({ type: 'success', text: 'Link copiado.' });
    } catch (err) {
      setMessage({ type: 'error', text: url });
    }
  };

  if (!userId) {
    return (
      <section className="forge-page admin-page">
        <div className="forge-inline-alert">
          <StatusPill color="amber">Sin sesión</StatusPill>
          <span>Conecta GitHub para entrar al panel.</span>
        </div>
      </section>
    );
  }

  if (currentUser && !currentUser.can_admin) {
    return (
      <section className="forge-page admin-page">
        <div className="forge-inline-alert">
          <StatusPill color="red">Restringido</StatusPill>
          <span>Tus roles actuales son {(currentUser.roles || []).join(', ')}.</span>
        </div>
      </section>
    );
  }

  return (
    <section className="forge-page admin-page">
      <div className="forge-page-header">
        <div>
          <div className="forge-section-eyebrow">Administración</div>
          <h1 className="forge-title">Centro de administración</h1>
          <p className="forge-lead">Personas, Academia FORGE, preparación de equipos y configuración de la instancia.</p>
        </div>
        <StatusPill
          color={currentUser?.can_admin ? 'green' : 'amber'}
          title={(currentUser?.roles || []).join(', ') || undefined}
        >
          {currentUser?.roles?.length ? `${currentUser.roles.length} roles activos` : 'Cargando'}
        </StatusPill>
      </div>

      {roleSetupRequested && (
        <div className="forge-inline-alert">
          <StatusPill color="blue">Preparación del proyecto</StatusPill>
          <span>Asigna los roles pendientes o crea invitaciones antes de que el flujo llegue a sus gates técnicos.</span>
        </div>
      )}

      {message && (
        <div className={`admin-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <nav className="admin-section-nav" aria-label="Secciones de administración">
        <a href="#admin-summary"><ForgeIcon name="home" /><span><strong>Resumen</strong><small>Estado general</small></span></a>
        <a href="#people-access"><ForgeIcon name="users" /><span><strong>Personas y acceso</strong><small>Usuarios, roles e invitaciones</small></span></a>
        <a href="#academy-admin"><ForgeIcon name="learn" /><span><strong>Academia FORGE</strong><small>Curso, roles y versiones</small></span></a>
        <a href="#readiness-admin"><ForgeIcon name="board" /><span><strong>Preparación y equipos</strong><small>Grupos y acompañamiento</small></span></a>
        <a href="#github-integration"><ForgeIcon name="github" /><span><strong>Integración GitHub</strong><small>Webhook global de FORGE</small></span></a>
        <a href="#instance-settings"><ForgeIcon name="paint" /><span><strong>Configuración</strong><small>Identidad visual</small></span></a>
      </nav>

      <div className="forge-metric-row admin-metrics" id="admin-summary">
        <div className="forge-metric">
          <span className="label">Usuarios</span>
          <span className="value">{users.length}</span>
        </div>
        <div className="forge-metric">
          <span className="label">Invitaciones</span>
          <span className="value">{pendingInvites}</span>
        </div>
        <div className="forge-metric">
          <span className="label">Marca</span>
          <span className="value small">{brandForm.brand_name}</span>
        </div>
        <div className="forge-metric">
          <span className="label">Estado</span>
          <span className="value small">{loading ? 'Sync' : 'Live'}</span>
        </div>
      </div>

      <div className="admin-block-heading"><div className="forge-section-eyebrow">Personas y acceso</div><h2>Quién participa y con qué responsabilidad</h2></div>
      <div className="admin-grid" id="people-access">
        <section className="forge-panel admin-panel">
          <div className="admin-panel-head">
            <ForgeIcon name="users" />
            <h2>Usuarios</h2>
            <button
              type="button"
              className="admin-info-btn"
              aria-label="Ver guía de roles"
              onClick={() => setRolesOpen(true)}
            >
              Roles <span aria-hidden="true">i</span>
            </button>
          </div>
          <div className="admin-list">
            {users.map((user) => (
              <div className="admin-user-row" key={user.id}>
                <div className="admin-user-main">
                  {user.avatar_url && <img src={user.avatar_url} alt="" />}
                  <div>
                    <strong>@{user.username}</strong>
                    <span>ID {user.github_id}</span>
                  </div>
                </div>
                <div className="admin-role-control">
                  <div className="admin-role-badges">
                    {user.roles.map((roleValue) => (
                      <span className={`admin-role-badge ${roleMeta(roleValue).admin ? 'admin' : ''}`} key={roleValue}>
                        {roleMeta(roleValue).label}
                      </span>
                    ))}
                  </div>
                  <RoleCheckboxes
                    availableRoles={roles}
                    selectedRoles={user.roles}
                    labelPrefix={`Cambiar roles de @${user.username}`}
                    onChange={(nextRoles) => updateRoles(user.id, nextRoles)}
                  />
                </div>
              </div>
            ))}
            {users.length === 0 && <div className="forge-empty">Sin usuarios registrados.</div>}
          </div>
        </section>

        <section className="forge-panel admin-panel">
          <div className="admin-panel-head">
            <ForgeIcon name="admin" />
            <h2>Invitaciones</h2>
          </div>
          <form className="admin-form" onSubmit={createInvitation}>
            <label>
              Usuario GitHub
              <input
                value={inviteForm.github_username}
                onChange={(event) => setInviteForm((prev) => ({ ...prev, github_username: event.target.value }))}
                placeholder="octocat"
                required
              />
            </label>
            <fieldset className="admin-role-fieldset">
              <legend>Roles</legend>
              <RoleCheckboxes
                availableRoles={roles}
                selectedRoles={inviteForm.roles}
                labelPrefix="Roles para la invitación"
                onChange={(nextRoles) => setInviteForm((prev) => ({ ...prev, roles: nextRoles }))}
              />
            </fieldset>
            <label>
              Nota
              <input
                value={inviteForm.note}
                onChange={(event) => setInviteForm((prev) => ({ ...prev, note: event.target.value }))}
                placeholder="Acceso inicial"
              />
            </label>
            <button className="forge-button primary" type="submit" disabled={creatingInvite}>
              Crear invitación
            </button>
          </form>

          <div className="admin-invites">
            {invitations.map((invitation) => (
              <div className="admin-invite-row" key={invitation.id}>
                <div>
                  <strong>@{invitation.github_username}</strong>
                  <span>{invitation.roles.join(', ')}</span>
                </div>
                {invitation.accepted ? (
                  <StatusPill color="green">Aceptada</StatusPill>
                ) : (
                  <button type="button" className="forge-button" onClick={() => copyInvite(invitation.invite_url)}>
                    Copiar link
                  </button>
                )}
              </div>
            ))}
            {invitations.length === 0 && <div className="forge-empty">Sin invitaciones.</div>}
          </div>
        </section>
      </div>

      <div className="admin-block-heading" id="github-integration"><div className="forge-section-eyebrow">Integración de plataforma</div><h2>GitHub para todos los proyectos</h2></div>
      <section className="forge-panel admin-panel">
        <div className="admin-panel-head">
          <ForgeIcon name="github" />
          <h2>Webhook global</h2>
          <StatusPill color={githubIntegration?.configured ? 'green' : 'amber'}>
            {githubIntegration?.configured ? 'Configurado' : 'Pendiente'}
          </StatusPill>
        </div>
        <p className="forge-muted">
          Un solo secreto protege los eventos GitHub de todos los repositorios conectados a FORGE. No pertenece a un proyecto ni se muestra en pantalla.
        </p>
        {githubIntegration?.source === 'local_runtime' && <div className="forge-inline-alert">
          <StatusPill color="blue">Modo local</StatusPill>
          <span>La validación usa el fallback local. No se crea, rota ni sincroniza ningún recurso de AWS o GitHub.</span>
        </div>}
        {githubIntegration?.source === 'ssm' && <p className="forge-muted">Modo plataforma: el secreto global está resguardado en SSM.</p>}
        {!githubIntegration?.public_endpoint_ready && githubIntegration?.runtime_mode === 'platform' && <div className="forge-inline-alert">
          <StatusPill color="amber">URL HTTPS pendiente</StatusPill>
          <span>Configura PUBLIC_API_URL antes de crear o sincronizar webhooks.</span>
        </div>}
        <div className="forge-button-row">
          {!githubIntegration?.configured && <button className="forge-button primary" type="button" disabled={managingGithubIntegration || !githubAction('initialize').available} title={githubAction('initialize').reason} onClick={() => manageGithubIntegration('initialize')}>Generar secreto seguro</button>}
          {githubIntegration?.configured && <button className="forge-button" type="button" disabled={managingGithubIntegration || !githubAction('validate').available} title={githubAction('validate').reason} onClick={() => manageGithubIntegration('validate')}>Validar</button>}
          {githubIntegration?.configured && <button className="forge-button" type="button" disabled={managingGithubIntegration || !githubAction('sync-webhooks').available} title={githubAction('sync-webhooks').reason} onClick={() => manageGithubIntegration('sync-webhooks')}>Sincronizar webhooks</button>}
          {githubIntegration?.configured && <button className="forge-button" type="button" disabled={managingGithubIntegration || !githubAction('rotate').available} title={githubAction('rotate').reason} onClick={() => manageGithubIntegration('rotate')}>Rotar secreto</button>}
        </div>
        {!githubIntegration?.configured && !githubAction('initialize').available && <p className="forge-muted">{githubAction('initialize').reason}</p>}
        {githubIntegration?.configured && <p className="forge-muted">{['validate', 'sync-webhooks', 'rotate'].map((action) => !githubAction(action).available ? githubAction(action).reason : null).filter(Boolean).join(' ')}</p>}
        <small className="forge-muted">Solo Forge Master y Admin pueden administrar esta integración. Los secretos AWS de cada proyecto se mantienen separados.</small>
      </section>

      <div className="admin-block-heading" id="instance-settings"><div className="forge-section-eyebrow">Configuración</div><h2>Identidad de la instancia</h2></div>
      <section className="forge-panel admin-panel brand-panel">
        <div className="admin-panel-head">
          <ForgeIcon name="paint" />
          <h2>Marca</h2>
        </div>
        <form className="admin-form brand-form" onSubmit={saveBrand}>
          <label>
            Nombre
            <input
              value={brandForm.brand_name}
              onChange={(event) => setBrandForm((prev) => ({ ...prev, brand_name: event.target.value }))}
              required
            />
          </label>
          <label>
            Subtítulo
            <input
              value={brandForm.brand_subtitle}
              onChange={(event) => setBrandForm((prev) => ({ ...prev, brand_subtitle: event.target.value }))}
              required
            />
          </label>
          <label>
            Logo URL
            <input
              value={brandForm.logo_url}
              onChange={(event) => setBrandForm((prev) => ({ ...prev, logo_url: event.target.value }))}
              required
            />
          </label>
          <label>
            Favicon URL
            <input
              value={brandForm.favicon_url}
              onChange={(event) => setBrandForm((prev) => ({ ...prev, favicon_url: event.target.value }))}
              required
            />
          </label>
          <label>
            Color principal
            <input
              type="color"
              value={brandForm.primary_color}
              onChange={(event) => setBrandForm((prev) => ({ ...prev, primary_color: event.target.value }))}
              required
            />
          </label>
          <label>
            Color acento
            <input
              type="color"
              value={brandForm.accent_color}
              onChange={(event) => setBrandForm((prev) => ({ ...prev, accent_color: event.target.value }))}
              required
            />
          </label>
          <button className="forge-button primary" type="submit" disabled={savingBrand}>
            Guardar marca
          </button>
        </form>
      </section>

      <div id="academy-admin"><div className="admin-block-heading"><div className="forge-section-eyebrow">Academia FORGE</div><h2>Formación oficial y certificación</h2></div><LearningAdmin apiUrl={apiUrl} /></div>
      <div id="readiness-admin"><div className="admin-block-heading"><div className="forge-section-eyebrow">Preparación y equipos</div><h2>Acompañamiento organizacional</h2></div><TeamAdmin apiUrl={apiUrl} users={users} /></div>

      {rolesOpen && (
        <div className="admin-modal-backdrop" role="presentation" onClick={() => setRolesOpen(false)}>
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="roles-dialog-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-modal-head">
              <div>
                <div className="forge-section-eyebrow">Roles</div>
                <h2 id="roles-dialog-title">Guía de asignación</h2>
              </div>
              <button type="button" className="admin-modal-close" onClick={() => setRolesOpen(false)}>
                Cerrar
              </button>
            </div>

            <div className="roles-guide-grid">
              <div className="roles-guide-list">
                {roles.map((role) => {
                  const meta = roleMeta(role.value);
                  return (
                    <article className="role-guide-card" key={role.value}>
                      <div>
                        <strong>{meta.label}</strong>
                        {meta.admin && <span>Admin</span>}
                      </div>
                      <p>{meta.description}</p>
                      <small>{meta.bestFor}</small>
                    </article>
                  );
                })}
              </div>

              <form className="role-advisor" onSubmit={(event) => event.preventDefault()}>
                <h3>Formulario rápido</h3>
                <label>
                  Responsabilidad principal
                  <select
                    value={roleAdvisor.responsibility}
                    onChange={(event) =>
                      setRoleAdvisor((prev) => ({ ...prev, responsibility: event.target.value }))
                    }
                  >
                    {roleAdvisorOptions.map((option) => (
                      <option value={option.value} key={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  ¿Necesita administrar usuarios o marca?
                  <select
                    value={roleAdvisor.adminAccess}
                    onChange={(event) =>
                      setRoleAdvisor((prev) => ({ ...prev, adminAccess: event.target.value }))
                    }
                  >
                    <option value="no">No</option>
                    <option value="yes">Sí</option>
                  </select>
                </label>
                <div className="role-recommendation">
                  <span>Rol recomendado</span>
                  <strong>{roleMeta(recommendedRole).label}</strong>
                  <p>{roleMeta(recommendedRole).description}</p>
                </div>
                <button
                  type="button"
                  className="forge-button"
                  onClick={() => setInviteForm((prev) => ({ ...prev, roles: [recommendedRole] }))}
                >
                  Usar en invitación
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Admin;
